import { useRef, useMemo } from 'react';
import { Container, IconButton, Tooltip } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { Button, Text } from 'components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';
import useGetCarriers from './queries/useGetCarriers.jsx';

const CustomSIPHeadersTable = ({
    tableData,
    isLoading,
    isRefetching,
    isFetching,
    refetch,
    handleAdd,
    handleEdit,
}) => {
    const gridRef = useRef();
    const { darkMode } = useSelector((state) => state.settings);
    const { data: carriersData } = useGetCarriers();

    const baseColDefs = useMemo(() => {
        return [
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                minWidth: 150,
                sortable: true,
            },
            {
                field: 'suffix',
                headerName: 'Suffix',
                flex: 1,
                minWidth: 150,
                sortable: true,
                cellRenderer: (params) => {
                    return params.value ? (
                        <Text>
                            <span style={{ color: '#666' }}>X-TRE-C-</span>
                            {params.value}
                        </Text>
                    ) : (
                        ''
                    );
                },
            },
            {
                field: 'customHeaderTypeID',
                headerName: 'Type',
                width: 130,
                sortable: true,
            },
            {
                field: 'linkID',
                headerName: 'Link Object Name',
                flex: 1,
                minWidth: 180,
                sortable: true,
                cellRenderer: (params) => {
                    const linkID = params.value;

                    // Look up carrier name from carriersData
                    if (linkID && carriersData) {
                        const carrier = carriersData.find(
                            (c) => c.id === linkID,
                        );
                        return carrier ? carrier.name : linkID;
                    }

                    return linkID || '';
                },
            },
            {
                field: 'value',
                headerName: 'Value',
                flex: 2,
                minWidth: 250,
                sortable: true,
            },
            {
                headerName: 'Action',
                lockPosition: 'right',
                width: 80,

                sortable: false,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                cellRenderer: (params) => {
                    return (
                        <Tooltip label="Edit">
                            <IconButton
                                aria-label="Edit"
                                icon={<BsThreeDotsVertical />}
                                size="sm"
                                variant="ghost"
                                color={darkMode ? 'white' : 'black'}
                                onClick={() => handleEdit(params)}
                                _hover={{
                                    bg: darkMode
                                        ? 'whiteAlpha.200'
                                        : 'blackAlpha.100',
                                }}
                                _active={{
                                    bg: darkMode
                                        ? 'whiteAlpha.300'
                                        : 'blackAlpha.200',
                                }}
                                _focus={{ boxShadow: 'none' }}
                            />
                        </Tooltip>
                    );
                },
                cellStyle: { display: 'flex', alignItems: 'center' },
            },
        ];
    }, [darkMode, handleEdit, carriersData]);

    // Use the column state hook to manage column visibility
    const { columns: colDefs, onColumnVisible } = useColumnState(baseColDefs);

    const isFetchingData = isLoading || isRefetching || isFetching;

    // Build table data object with columns from hook
    const tableDataWithColumns = useMemo(
        () => ({
            columns: colDefs,
            data: tableData.data,
        }),
        [colDefs, tableData.data],
    );

    return (
        <Container
            as="div"
            maxW="container.xl"
            px={0}
            aria-label="custom sip headers main table">
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Custom SIP Headers
            </Text>
            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isFetchingData}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                overlayNoRowsTemplate="No Custom SIP Headers Found"
                titleName="Custom SIP Headers"
                extraComponents={
                    <div style={{ paddingLeft: '20px' }}>
                        <Button size="sm" width="140px" onClick={handleAdd}>
                            + Add Header
                        </Button>
                    </div>
                }
            />
        </Container>
    );
};

export default CustomSIPHeadersTable;
